import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import {
  RecoveryWipHeader,
  RecoveryWipTabs,
  WipActionDescription,
  wipActionProps,
} from "@/features/recovery/recovery-wip";

/**
 * RCV-01 — Backup Ceremony, ported from
 * `.design-ref/prototype/features/recovery/backup-ceremony.tsx` (ALL-5825).
 *
 * No recovery backend exists in this environment, and this is a
 * custody-adjacent ceremony (it generates and distributes key shards), so the
 * port keeps the designer's structure — the breadcrumb, the header with its
 * Checklist / Complete switcher, the session panel with its ordered checklist,
 * the "Previous Backup" panel — and fills none of it: the session id and the
 * previous backup are the shared "not available yet" marker, the checklist is
 * a designed empty state, and every control (the switcher, "Begin Step 2") is
 * disabled because it would drive a ceremony that does not exist here.
 *
 * The designer's "Complete" success card is deliberately not reachable: it
 * asserts five distributed shards and confirmed receipts, and no such ceremony
 * has run.
 */
export default function RecoveryBackupPage() {
  return (
    <div>
      <RecoveryWipHeader
        title="Backup Ceremony"
        description="Generate and distribute a new set of key shards to custodians. This area has no backend in this environment yet."
        actions={<RecoveryWipTabs label="Backup ceremony view" tabs={["Checklist", "Complete"]} active="Checklist" />}
      />
      <WipActionDescription />

      <div className="space-y-5">
        <div className="rounded-[10px] border border-line bg-surface p-6">
          <div className="mb-4 flex flex-wrap items-center gap-2 text-base font-bold text-ink">
            Backup Session <UnavailableValue />
          </div>

          <EmptyState
            className="py-6"
            title="No backup session to run"
            description="No key-backup ceremony is scheduled or in progress, so there are no steps to work through."
            meta="Checklist steps and their completion come from the recovery backend. Nothing here is estimated."
          />

          <Button className="mt-4" {...wipActionProps()}>
            Begin Step 2
          </Button>
        </div>

        <div className="rounded-[10px] border border-line bg-surface p-6">
          <div className="mb-1 text-sm font-bold text-ink">Previous Backup</div>
          <div className="font-mono text-xs text-muted">
            <UnavailableValue />
          </div>
        </div>
      </div>
    </div>
  );
}
